import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiCmdService } from '../../../services/api-cmd.service';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { PrivacyEventsRequest } from '@packages/api-cmd-type-definitions/lib/privacy/events.dto';
import { HasRoleModule } from '../../../modules/has-role.module';
import { MatDatetimepickerModule, MatNativeDatetimeModule } from '@mat-datetimepicker/core';
import {
  ACTION_LANGUAGES,
  EVENT_CATEGORIES,
  EVENT_TYPES_DOCUMENTS,
  EVENT_TYPES_POLICIES,
  EVENT_TYPES_PURPOSES,
  getActionLanguageLabel,
  getEventCategoryLabel,
  getEventTypeDocumentLabel,
  getEventTypePolicyLabel,
  getEventTypePurposeLabel,
  PROOFS,
  SOURCES,
} from '../../../constants/privacy.constants';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { handlerSnackbarApiError } from '../../../utils/snackbar.handler';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  DataListPrivacyPolicyCodesApiResponseDto,
  DataListPrivacyDocumentCodesApiResponseDto,
  DataListPrivacyPurposeCodesApiResponseDto,
  GetPrivacyPurposeCodeResponse,
  GetPrivacyPolicyCodeResponse,
  GetPrivacyDocumentCodeResponse,
} from '@packages/api-cmd-type-definitions';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { TOUCHPOINT_WEB_APP_CMD } from '../../../constants/touchpointCode.constant';
import { TOUCHPOINT_PERSON_INGRESS_ROLE_TYPES } from '../../../constants/touchpointPersonIngress.constants';

@Component({
  selector: 'app-create-person',
  standalone: true,
  imports: [
    MatCard,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatButton,
    MatLabel,
    MatIcon,
    MatIconButton,
    NgForOf,
    MatInputModule,
    HasRoleModule,
    MatDatetimepickerModule,
    MatNativeDatetimeModule,
    MatSelect,
    MatOption,
    MatProgressSpinner,
    NgIf,
    MatAutocompleteTrigger,
    MatAutocomplete,
    AsyncPipe,
  ],
  templateUrl: './create-person.component.html',
  styleUrl: './create-person.component.scss',
})
export class CreatePersonComponent implements OnInit {
  public isSubmitting = false;
  creationForm: FormGroup;
  touchpointPersonIngressRoleTypes = TOUCHPOINT_PERSON_INGRESS_ROLE_TYPES;
  privacyEventCategories = EVENT_CATEGORIES;
  privacyEventTypesPolicies = EVENT_TYPES_POLICIES;
  privacyEventTypesDocuments = EVENT_TYPES_DOCUMENTS;
  privacyEventTypesPurposes = EVENT_TYPES_PURPOSES;
  privacyProofs = PROOFS;
  privacySources = SOURCES;
  actionLanguages = ACTION_LANGUAGES;
  getActionLanguage = getActionLanguageLabel;
  getEventCategory = getEventCategoryLabel;
  getEventTypePolicy = getEventTypePolicyLabel;
  getEventTypeDocument = getEventTypeDocumentLabel;
  getEventTypePurpose = getEventTypePurposeLabel;
  privacyCodesPolicies: DataListPrivacyPolicyCodesApiResponseDto['data'] = [];
  privacyCodesDocuments: DataListPrivacyDocumentCodesApiResponseDto['data'] = [];
  privacyCodesPurposes: DataListPrivacyPurposeCodesApiResponseDto['data'] = [];
  filteredCodes: Observable<
    DataListPrivacyPolicyCodesApiResponseDto['data'] | DataListPrivacyDocumentCodesApiResponseDto['data'] | DataListPrivacyPurposeCodesApiResponseDto['data']
  >[] = [];
  uploadedFileNames: { [key: number]: string } = {};

  constructor(
    private fb: FormBuilder,
    private apiCmdService: ApiCmdService,
    private router: Router,
    private snackbar: MatSnackBar
  ) {
    this.creationForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      fieraId: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      fiscalCode: [''],
      personPrivacy: this.fb.group({
        action: this.fb.group({
          language: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]],
          date: ['', Validators.required],
          touchpoint: [''],
          proof: this.fb.array([
            this.fb.group({
              code: ['', Validators.required],
              value: ['', Validators.required],
            }),
          ]),
          source: this.fb.array([
            this.fb.group({
              code: ['', Validators.required],
              value: ['', Validators.required],
            }),
          ]),
        }),
        events: this.fb.array(
          [
            this.fb.group({
              category: ['', Validators.required],
              type: ['', Validators.required],
              code: ['', Validators.required],
            }),
          ],
          [Validators.required, Validators.minLength(1)]
        ),
      }),
      touchpointPersonIngress: this.fb.group({
        ingressRole: ['', Validators.required],
        personType: [''],
      }),
    });
  }

  ngOnInit(): void {
    this.fetchPrivacyCodes();
  }

  get proofs(): FormArray {
    return this.creationForm.get('personPrivacy.action.proof') as FormArray;
  }

  addProof(): void {
    this.proofs.push(
      this.fb.group({
        code: ['', Validators.required],
        value: ['', Validators.required],
      })
    );
  }

  removeProof(index: number): void {
    this.proofs.removeAt(index);
  }

  get sources(): FormArray {
    return this.creationForm.get('personPrivacy.action.source') as FormArray;
  }

  addSource(): void {
    this.sources.push(
      this.fb.group({
        code: ['', Validators.required],
        value: ['', Validators.required],
      })
    );
  }

  removeSource(index: number): void {
    this.sources.removeAt(index);
  }

  get events(): FormArray {
    return this.creationForm.get('personPrivacy.events') as FormArray;
  }

  addEvent(): void {
    this.events.push(
      this.fb.group({
        category: ['', Validators.required],
        type: ['', Validators.required],
        code: ['', Validators.required],
      })
    );
    this.filteredCodes.push(new Observable());
  }

  removeEvent(index: number): void {
    this.events.removeAt(index);
    this.filteredCodes.splice(index, 1);
  }

  onCategoryChange(event: any, index: number): void {
    const selectedCategory = event.value;
    const eventGroup = this.events.at(index) as FormGroup;
    eventGroup.get('type')?.setValue('');
    eventGroup.get('code')?.setValue('');
    this.filteredCodes[index] = eventGroup.get('code')!.valueChanges.pipe(
      startWith(''),
      map((value: string) => this.filterCodes(value, selectedCategory))
    );
  }

  getEventTypeFromCategory(category: string): { label: string; value: string }[] {
    switch (category) {
      case 'policies':
        return this.privacyEventTypesPolicies;
      case 'documents':
        return this.privacyEventTypesDocuments;
      case 'purposes':
        return this.privacyEventTypesPurposes;
      default:
        return [];
    }
  }

  fetchPrivacyCodes(): void {
    this.apiCmdService.listPrivacyPolicyCodes({}).subscribe(response => {
      this.privacyCodesPolicies = response.data;
    });
    this.apiCmdService.listPrivacyDocumentCodes({}).subscribe(response => {
      this.privacyCodesDocuments = response.data;
    });
    this.apiCmdService.listPrivacyPurposeCodes({}).subscribe(response => {
      this.privacyCodesPurposes = response.data;
    });
  }

  filterCodes(
    value: string,
    category: string
  ): DataListPrivacyPolicyCodesApiResponseDto['data'] | DataListPrivacyDocumentCodesApiResponseDto['data'] | DataListPrivacyPurposeCodesApiResponseDto['data'] {
    const filterValue = value.toLowerCase();
    const codes = this.getEventCodes(category);
    return codes.filter(code => code.name.toLowerCase().includes(filterValue));
  }

  getLabelFromCode(code: GetPrivacyPurposeCodeResponse | GetPrivacyPolicyCodeResponse | GetPrivacyDocumentCodeResponse): string {
    // @ts-expect-error TS2339
    if (code.privacyPolicy) {
      return (
        code.name +
        ' (IP: ' +
        // @ts-expect-error TS2339
        code.privacyPolicy.name +
        ')'
      );
    }
    return code.name;
  }

  getEventCodes(
    category: string
  ): DataListPrivacyPolicyCodesApiResponseDto['data'] | DataListPrivacyDocumentCodesApiResponseDto['data'] | DataListPrivacyPurposeCodesApiResponseDto['data'] {
    switch (category) {
      case 'policies':
        return this.privacyCodesPolicies;
      case 'documents':
        return this.privacyCodesDocuments;
      case 'purposes':
        return this.privacyCodesPurposes;
      default:
        return [];
    }
  }

  handleFileInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const allowedTypes = ['image/*', 'application/pdf', 'text/*'];
      if (allowedTypes.some(type => file.type.match(type))) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          console.log(base64);
          this.proofs.at(index).get('value')?.setValue(base64);
          this.uploadedFileNames[index] = file.name;
        };
        reader.readAsDataURL(file);
      } else {
        console.error('Invalid file type');
      }
    }
  }

  private setterIsSubmitting(value: boolean): () => void {
    return () => {
      this.isSubmitting = value;
    };
  }

  submitForm() {
    if (this.creationForm.valid) {
      this.isSubmitting = true;
      const privacy: PrivacyEventsRequest = this.creationForm.value['personPrivacy'];
      if (privacy) {
        privacy.action.language = privacy.action.language.toLowerCase();
        if (!privacy.action.date) privacy.action.date = new Date();
        if (!privacy.action.touchpoint) delete privacy.action.touchpoint;
        if (privacy.action.proof?.length === 0) delete privacy.action.proof;
        if (privacy.action.source?.length === 0) delete privacy.action.source;
        if (privacy.events) {
          privacy.events = privacy.events.map(event => ({ code: event.code, type: event.type }));
        }
      }
      this.apiCmdService
        .createPerson({
          data: {
            fieraId: this.creationForm.value['fieraId'] || undefined,
            email: this.creationForm.value['email'],
            fiscalCode: this.creationForm.value['fiscalCode'] || undefined,
            firstName: this.creationForm.value['firstName'],
            lastName: this.creationForm.value['lastName'],
            touchpointCode: TOUCHPOINT_WEB_APP_CMD,
            personPrivacy: privacy ? [privacy] : undefined,
            personProfile: undefined,
            personContacts: undefined,
            personCompanyProfiles: undefined,
            touchpointPersonIngress: {
              ingressDate: new Date(),
              ingressRole: this.creationForm.value['touchpointPersonIngress']['ingressRole'],
              touchpointCode: TOUCHPOINT_WEB_APP_CMD,
              personType: this.creationForm.value['touchpointPersonIngress']['personType'] || 'NA',
            },
          },
        })
        .subscribe({
          next: response => {
            this.isSubmitting = false;
            const personId = response.data;
            this.router.navigate(['/persone/edit', personId]);
          },
          error: err => handlerSnackbarApiError(this.snackbar, err, [this.setterIsSubmitting(false)]),
        });
    } else {
    }
  }

  navigateBack() {
    this.router.navigate(['/persone']);
  }

  protected readonly Object = Object;
}
